import React, { useState, forwardRef } from "react";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
import Loading from "../loading/Loading";
import { toast } from "react-toastify"; // ✅ Import Toastify
import "react-toastify/dist/ReactToastify.css"; // ✅ Import Toastify styles
import "./CartDropdown.css";

const CartDropdown = forwardRef(({ onClose }, ref) => {
  const { cart, removeFromCart, clearCart, updateQuantity } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const getTotalPrice = () => {
    return cart.reduce((total, item) => {
      let itemPrice = Array.isArray(item.price)
        ? item.price[0]?.price || 0 // Extract price if it's an array, default to 0 if undefined
        : item.price ?? 0; // Use item.price directly if not an array, default to 0 if null/undefined

      return total + itemPrice * item.quantity;
    }, 0);
  };

  const incrementQuantity = (item) => {
    updateQuantity(item.id, item.variant, item.quantity + 1);
    toast.success(`🔼 Increased "${item.name}" quantity`);
  };

  const decrementQuantity = (item) => {
    if (item.quantity > 1) {
      updateQuantity(item.id, item.variant, item.quantity - 1);
      toast.info(`⬇️ Reduced "${item.name}" quantity`);
    }
  };

  const handleRemoveItem = (item) => {
    removeFromCart(item.id, item.variant);
    toast.error(`${item.name} removed from cart`);
  };

  const handleClearCart = () => {
    clearCart();
    toast.warn("Cart cleared!");
  };

  const handleBookNow = () => {
    if (cart.length > 0) {
      setIsLoading(true);
      setTimeout(() => {
        navigate(`/tours/book-now`, {
          state: {
            cart,
            totalPrice: getTotalPrice(),
          },
        });
        window.scrollTo(0, 0);
        setIsLoading(false);
        toast.success("Proceeding to booking!");
      }, 1000);
    } else {
      toast.error("Your cart is empty.");
    }
  };

  return (
    <>
      {isLoading && <Loading />}
      <div className="cart-dropdown" ref={ref}>
        <h4>Cart Summary</h4>
        {cart.length > 0 ? (
          <>
            <ul>
              {cart.map((item, index) => (
                <li key={`${item.id}-${index}`}>
                  <img
                    src={item.imgArray[0]}
                    alt={item.name}
                    className="cart-item-img"
                    loading="lazy"
                  />
                  <div className="cart-item-details">
                    <span className="cart-item-name">{item.name}</span>
                    <span className="cart-item-variant">
                      Variant: {item.variant}
                    </span>
                    <div className="cart-item-prices">
                      <span className="cart-item-price">
                        Price:{" "}
                        {item.price === 0
                          ? "Free"
                          : item.price === null
                          ? "Contact Us"
                          : `₱${
                              Array.isArray(item.price)
                                ? item.price[0].price // Extract price from the first object in the array
                                : item.price
                            }`}
                      </span>

                      <span className="cart-item-priceTotal">
                        Total:{" "}
                        {item.price === 0
                          ? "Free"
                          : item.price === null
                          ? "Contact Us"
                          : `₱${
                              Array.isArray(item.price)
                                ? (
                                    item.price[0].price * item.quantity
                                  ).toLocaleString()
                                : (item.price * item.quantity).toLocaleString()
                            }`}{" "}
                        ({item.quantity})
                      </span>
                    </div>

                    <div className="quantity-controls">
                      <button
                        onClick={() => decrementQuantity(item)}
                        className="quantity-btn"
                      >
                        −
                      </button>
                      <span className="cart-item-quantity">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => incrementQuantity(item)}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item)}
                      className="remove-item"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <p className="cart-total">
              Total: ₱
              {isNaN(getTotalPrice())
                ? "0.00"
                : Number(getTotalPrice()).toFixed(2)}
            </p>

            <button onClick={handleBookNow} className="book-now">
              Book Now
            </button>
            <button onClick={handleClearCart} className="clear-cart">
              Clear Cart
            </button>
          </>
        ) : (
          <p>Your cart is empty.</p>
        )}
      </div>
    </>
  );
});

export default CartDropdown;
